import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { playMiniTaskSound } from '../soundEffects';
import OddOneOutShape, { oddOneOutShapeLabel } from './OddOneOutShape';
import {
  countCorrectEvents,
  gridColumnsForItemCount,
  instructionForUniqueCount,
  replayOddOneOutEvents,
} from './oddOneOutBoard';

const COUNTDOWN_STEP_MS = 700;
const BOARD_COMPLETE_DELAY_MS = 350;

function MetricCard({ icon, label, value, color }) {
  return (
    <Box sx={{ minWidth: { xs: 74, sm: 96, md: 118 }, borderRadius: 3, bgcolor: '#fff', border: `3px solid ${color}`, px: { xs: 0.8, md: 1.4 }, py: { xs: 0.6, md: 0.9 }, display: 'flex', alignItems: 'center', gap: { xs: 0.4, md: 0.8 }, boxShadow: '0 4px 0 rgba(76,29,149,0.10)' }}>
      {icon}
      <Box>
        <Typography sx={{ color: '#6d5d93', fontWeight: 900, fontSize: { xs: 10, md: 12 }, lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 21, md: 28 }, lineHeight: 1.05 }}>{value}</Typography>
      </Box>
    </Box>
  );
}

export default function OddOneOutRoundView({
  round,
  classTitle,
  soundEnabled,
  onToggleSound,
  onExit,
  onComplete,
}) {
  const boards = useMemo(() => round.boards || [], [round.boards]);
  const [phase, setPhase] = useState('intro');
  const [countdown, setCountdown] = useState(3);
  const [events, setEvents] = useState([]);
  const [displayBoardIndex, setDisplayBoardIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(Number(round.duration_seconds || 40));
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [exitOpen, setExitOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const eventsRef = useRef([]);
  const completionStartedRef = useRef(false);
  const finishReasonRef = useRef('timer');
  const playStartedAtRef = useRef('');
  const playDeadlineAtRef = useRef('');
  const boardTimerRef = useRef(null);

  const currentBoard = boards[displayBoardIndex] || null;
  const replay = useMemo(() => replayOddOneOutEvents(boards, events), [boards, events]);
  const score = useMemo(() => countCorrectEvents(boards, events), [boards, events]);
  const selectedCurrent = currentBoard
    ? replay.selectedByBoard[String(currentBoard.board_id)] || new Set()
    : new Set();
  const correctCurrent = currentBoard
    ? replay.correctByBoard[String(currentBoard.board_id)] || new Set()
    : new Set();
  const solutionCurrent = new Set((currentBoard?.solution_item_ids || []).map(String));
  const columns = gridColumnsForItemCount(currentBoard?.items?.length || 0);
  const rows = Math.max(1, Math.ceil((currentBoard?.items?.length || 1) / columns));
  const gridRatio = columns / rows;
  const gridHeightWidth = {
    xs: `calc(${Math.round(gridRatio * 10000) / 100}dvh - ${Math.round(gridRatio * 310)}px)`,
    sm: `calc(${Math.round(gridRatio * 10000) / 100}dvh - ${Math.round(gridRatio * 310)}px)`,
    md: `calc(${Math.round(gridRatio * 10000) / 100}dvh - ${Math.round(gridRatio * 320)}px)`,
  };
  const targetCount = Number(round.unique_count || solutionCurrent.size || 1);

  const finish = useCallback(async (finishReason = 'timer') => {
    if (completionStartedRef.current) return;
    completionStartedRef.current = true;
    finishReasonRef.current = finishReason;
    setPhase('completing');
    setError('');
    try {
      await onComplete(eventsRef.current, finishReason);
    } catch (completionError) {
      completionStartedRef.current = false;
      setError(completionError?.message || 'Sonucun kaydedilemedi.');
      setPhase('completion_error');
    }
  }, [onComplete]);

  useEffect(() => {
    if (phase !== 'intro') return undefined;
    const timer = window.setTimeout(() => setPhase('countdown'), 900);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'countdown') return undefined;
    if (countdown <= 0) {
      const started = Date.now();
      playStartedAtRef.current = new Date(started).toISOString();
      playDeadlineAtRef.current = new Date(started + Number(round.duration_seconds || 40) * 1000).toISOString();
      setPhase('playing');
      return undefined;
    }
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), COUNTDOWN_STEP_MS);
    return () => window.clearTimeout(timer);
  }, [countdown, phase, round.duration_seconds]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const updateClock = () => {
      const deadlineMs = Date.parse(playDeadlineAtRef.current);
      const remainingMs = deadlineMs - Date.now();
      if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
        setRemainingSeconds(0);
        finish('timer');
        return;
      }
      setRemainingSeconds(Math.ceil(remainingMs / 1000));
    };
    updateClock();
    const timer = window.setInterval(updateClock, 100);
    document.addEventListener('visibilitychange', updateClock);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', updateClock);
    };
  }, [finish, phase]);

  useEffect(() => () => {
    if (boardTimerRef.current) window.clearTimeout(boardTimerRef.current);
  }, []);

  const handleSelect = (item) => {
    if (phase !== 'playing' || transitioning || !currentBoard) return;
    if (Date.now() >= Date.parse(playDeadlineAtRef.current)) {
      finish('timer');
      return;
    }
    const boardId = String(currentBoard.board_id);
    const itemId = String(item.item_id);
    if (selectedCurrent.has(itemId)) return;
    const startedMs = Date.parse(playStartedAtRef.current);
    const maxElapsed = Number(round.duration_seconds || 40) * 1000;
    const elapsedMs = Math.max(0, Math.min(maxElapsed, Math.round(Date.now() - startedMs)));
    const nextEvents = [...eventsRef.current, { board_id: boardId, item_id: itemId, elapsed_ms: elapsedMs }];
    eventsRef.current = nextEvents;
    setEvents(nextEvents);

    const correct = solutionCurrent.has(itemId);
    playMiniTaskSound(correct ? 'correct' : 'wrong', soundEnabled);
    if (!correct) {
      setMessage('Bu görselin bir eşi var. Devam et!');
      window.setTimeout(() => setMessage(''), BOARD_COMPLETE_DELAY_MS);
      return;
    }
    const foundAfter = new Set(correctCurrent);
    foundAfter.add(itemId);
    if (foundAfter.size < solutionCurrent.size) {
      setMessage(`${foundAfter.size} / ${solutionCurrent.size} bulundu`);
      return;
    }

    setMessage('Harika! Hepsini buldun.');
    setTransitioning(true);
    boardTimerRef.current = window.setTimeout(() => {
      const nextIndex = displayBoardIndex + 1;
      setDisplayBoardIndex(nextIndex);
      setTransitioning(false);
      setMessage('');
      if (nextIndex >= boards.length) finish('queue_exhausted');
    }, BOARD_COMPLETE_DELAY_MS);
  };

  const retryStart = () => {
    setError('');
    setCountdown(0);
    setPhase('countdown');
  };

  const retryCompletion = () => finish(finishReasonRef.current);
  const instruction = message || instructionForUniqueCount(targetCount);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffaf0', color: '#172554', overflow: 'hidden' }}>
      {round.preview && (
        <Box sx={{ position: 'fixed', bottom: 10, left: 10, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.4, py: 0.45, borderRadius: 99, fontWeight: 1000 }}>
          TEST MODU
        </Box>
      )}
      <Box sx={{ mx: 'auto', maxWidth: 1720, px: { xs: 1.2, sm: 2, md: 3.2 }, pb: 2 }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'auto 1fr', sm: 'auto 1fr auto', md: '220px 1fr auto' }, alignItems: 'center', columnGap: { xs: 0.7, md: 2 }, rowGap: { xs: 0.45, sm: 0 }, mt: { xs: 0.7, md: 1.2 }, mb: { xs: 0.8, md: 1.2 } }}>
          <ButtonBase onClick={() => setExitOpen(true)} disabled={phase === 'completing'} sx={{ gridColumn: { xs: 1, sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' }, borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: '#fff', color: '#33258a', px: { xs: 1, md: 2 }, minHeight: { xs: 48, md: 58 }, display: 'flex', gap: 0.5, boxShadow: '0 4px 0 rgba(91,33,182,0.12)' }}>
            <ArrowBackRoundedIcon />
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 1000 }}>Ana sayfa</Typography>
          </ButtonBase>
          <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 1, sm: 'auto' }, textAlign: 'center', minWidth: 0 }}>
            <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 24, sm: 36, md: 42 }, lineHeight: 1, whiteSpace: 'nowrap' }}>Farklı Olanı Bul</Typography>
            <Typography sx={{ color: '#6d45df', fontWeight: 900, fontSize: { xs: 12, md: 17 }, lineHeight: 1.1 }}>{round?.is_endless ? 'Sonsuz Karma' : `Level ${round.level}`}</Typography>
          </Box>
          <Box sx={{ gridColumn: { xs: 2, sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' }, justifySelf: 'end', display: 'flex', gap: { xs: 0.4, md: 0.8 } }}>
            <MetricCard icon={<TimerRoundedIcon sx={{ color: '#7c3aed' }} />} label="SÜRE" value={remainingSeconds} color="#d8b4fe" />
            <MetricCard icon={<StarRoundedIcon sx={{ color: '#f59e0b' }} />} label="SKOR" value={score} color="#fde68a" />
          </Box>
        </Box>

        <Box sx={{ width: '100%', maxWidth: { xs: 560, sm: 760, md: 960 }, height: { xs: 'calc(100dvh - 198px)', sm: 'calc(100dvh - 205px)', md: 'calc(100dvh - 220px)' }, minHeight: { xs: 350, sm: 500, md: 520 }, mx: 'auto', borderRadius: { xs: 4, md: 6 }, bgcolor: 'rgba(255,255,255,0.96)', border: '3px solid #e9d5ff', boxShadow: '0 12px 0 rgba(91,33,182,0.10), 0 24px 50px rgba(76,29,149,0.12)', p: { xs: 1.2, sm: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
          {(phase === 'intro' || phase === 'countdown') && (
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Box sx={{ width: { xs: 92, md: 120 }, height: { xs: 92, md: 120 }, borderRadius: '50%', bgcolor: '#f3e8ff', color: '#7c3aed', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}>
                <SearchRoundedIcon sx={{ fontSize: { xs: 58, md: 76 } }} />
              </Box>
              <Typography sx={{ color: '#251777', fontWeight: 1000, fontSize: { xs: 28, sm: 40, md: 50 } }}>
                {phase === 'intro' ? instructionForUniqueCount(targetCount) : countdown}
              </Typography>
              <Typography sx={{ color: '#6d5d93', fontWeight: 800, fontSize: { xs: 16, md: 20 }, mt: 1 }}>40 saniyede olabildiğince çok bul.</Typography>
            </Box>
          )}

          {(phase === 'start_error' || phase === 'completion_error') && (
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <CloseRoundedIcon sx={{ color: '#ef4444', fontSize: 72 }} />
              <Typography sx={{ color: '#991b1b', fontWeight: 1000, fontSize: { xs: 25, md: 36 } }}>{error}</Typography>
              <Button variant="contained" onClick={phase === 'start_error' ? retryStart : retryCompletion} sx={{ mt: 2, borderRadius: 99, fontWeight: 1000 }}>Tekrar dene</Button>
            </Box>
          )}

          {phase === 'completing' && (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={62} />
              <Typography sx={{ mt: 2, color: '#251777', fontWeight: 1000, fontSize: { xs: 28, md: 42 } }}>Harika çalıştın!</Typography>
              <Typography sx={{ color: '#6d5d93', fontWeight: 900 }}>Sonucun kaydediliyor...</Typography>
            </Box>
          )}

          {phase === 'playing' && currentBoard && (
            <>
              <Box sx={{ minHeight: { xs: 40, md: 48 }, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.7, mb: { xs: 1, md: 1.5 }, px: 1.5, color: '#4c1d95' }}>
                {message && <StarRoundedIcon sx={{ color: '#facc15' }} />}
                <Typography aria-live="polite" sx={{ textAlign: 'center', fontWeight: 1000, fontSize: { xs: 16, sm: 20, md: 24 } }}>{instruction}</Typography>
              </Box>
              <Box sx={{ width: '100%', maxWidth: gridHeightWidth, display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: { xs: 0.65, sm: 1, md: 1.3 }, alignContent: 'center', mx: 'auto' }}>
                {(currentBoard.items || []).map((item, index) => {
                  const itemId = String(item.item_id);
                  const selected = selectedCurrent.has(itemId);
                  const correct = correctCurrent.has(itemId);
                  const label = oddOneOutShapeLabel(item.shape_key, item.pattern_key);
                  const statusLabel = selected ? (correct ? ', doğru bulundu' : ', yanlış seçim') : '';
                  return (
                    <ButtonBase
                      key={itemId}
                      aria-label={`${label}, şekil ${index + 1}${statusLabel}`}
                      disabled={selected || transitioning}
                      onClick={() => handleSelect(item)}
                      sx={{ aspectRatio: '1 / 1', minWidth: 0, minHeight: { xs: 52, sm: 66, md: 82 }, borderRadius: { xs: 2.2, md: 3 }, bgcolor: correct ? '#dcfce7' : selected ? '#fee2e2' : '#fff', border: `3px solid ${correct ? '#22c55e' : selected ? '#ef4444' : '#c4b5fd'}`, boxShadow: correct ? '0 4px 0 #16a34a' : selected ? '0 4px 0 #dc2626' : '0 5px 0 rgba(109,69,223,0.16)', p: { xs: 0.35, sm: 0.55, md: 0.8 }, position: 'relative', transition: 'transform 120ms ease, background-color 120ms ease', '&:hover': { bgcolor: '#faf5ff', transform: 'translateY(-2px)' }, '&:focus-visible': { outline: '5px solid #facc15', outlineOffset: 2 }, '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } } }}>
                      <OddOneOutShape shapeKey={item.shape_key} patternKey={item.pattern_key} rotationDeg={item.rotation_deg} />
                      {selected && (
                        <Box sx={{ position: 'absolute', right: 3, top: 3, width: { xs: 22, md: 28 }, height: { xs: 22, md: 28 }, borderRadius: '50%', bgcolor: correct ? '#22c55e' : '#ef4444', color: '#fff', display: 'grid', placeItems: 'center' }}>
                          {correct ? <CheckCircleRoundedIcon sx={{ fontSize: { xs: 19, md: 25 } }} /> : <CloseRoundedIcon sx={{ fontSize: { xs: 18, md: 24 } }} />}
                        </Box>
                      )}
                    </ButtonBase>
                  );
                })}
              </Box>
              {targetCount > 1 && (
                <Typography sx={{ mt: 1.2, textAlign: 'center', color: '#6d45df', fontWeight: 1000, fontSize: { xs: 15, md: 20 } }}>
                  {correctCurrent.size} / {targetCount} bulundu
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      <Dialog open={exitOpen} onClose={() => setExitOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, border: '2px solid #e9d5ff' } }}>
        <DialogTitle sx={{ color: '#251777', fontWeight: 1000 }}>Görevden çıkmak istiyor musun?</DialogTitle>
        <DialogContent><Typography sx={{ color: '#6d5d93', fontWeight: 800 }}>Süren durmaz; geri döndüğünde kaldığın yerden devam edersin.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExitOpen(false)} autoFocus sx={{ fontWeight: 900 }}>Göreve dön</Button>
          <Button color="error" variant="contained" onClick={onExit} sx={{ borderRadius: 99, fontWeight: 900 }}>Çık</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
